import { NgZone, Type } from "@angular/core";
import { GridsterPush } from "angular-gridster2";
import deepcopy from "deepcopy";
import { BehaviorSubject, Subject } from "rxjs";
import type { ModuleContainerComponent } from "src/app/dashboard/module-container/module-container.component";
import { ModuleManagerDateRangeInfo, ModuleManagerModuleSettings } from "src/shared/models/module-manager/module-manager-settings.models";
import { ClearViewUtils } from "../utils";
import { ModuleManagerEvent } from "./module-manager/module-manager-event.model";
import { ModuleManagerHeaderItem } from "./module-manager/module-manager-header-item.model";
import { IModuleManagerMenuItem, ModuleManagerCheckMenu, ModuleManagerMenuItem, ModuleManagerMenuItemPlacement, ModuleManagerMenuItemType, ModuleManagerSubMenu } from "./module-manager/module-manager-menu-item.model";
import { ModuleManagerOptions, ModuleManagerRefreshType } from "./module-manager/module-manager-options.model";
import { ModuleManagerSetting } from "./module-manager/module-manager-setting.model";
import { ModuleManagerState } from "./module-manager/module-manager-state.model";
import { IModuleManagerToolbarComponent } from "./module-manager/module-manager-toolbar-component.model";
import { ModuleManagerTransferData } from "./module-manager/module-manager-transfer-data.model";
import { ReportSelectorSettings } from "./pipeline-config/reporting-sets.models";
import SlideoutSettingsManager from "./slideout-settings-manager";

export class ModuleManager {
    /** The number of times a module will silenty retry on error before displaying to the user */
    static readonly MAX_LOAD_RETRIES = 3;
    static readonly TIME_BETWEEN_AUTO_RETRY = 5000; // ms

    public loadedStateValues: Record<string, any> = null;

    maxDateRange: number = Number.MAX_VALUE;

    minDate = new Date().addMonths(-25);
    slideoutSettingsManager = new BehaviorSubject<SlideoutSettingsManager>(null);
    numRetries = 0;
    transferChildData: any;
    hasCustomSettingsDirective = false;
    customSettings: { [settingName: string]: ModuleManagerSetting<any> } = {};
    moduleStates: { [stateName: string]: ModuleManagerState<any> } = {};
    previousStateValues: { [stateName: string]: any } = {};
    additionalMenuItems: { [menuItemName: string]: IModuleManagerMenuItem } = null;
    afterExportMenuItems: { [menuItemName: string]: IModuleManagerMenuItem } = null;
    additionalHeaderItems: { [headerItemName: string]: ModuleManagerHeaderItem } = null;
    isModuleSubscriptionPreview = false;
    moduleEvents: { [eventName: string]: ModuleManagerEvent } = {};
    moduleSettings: ModuleManagerModuleSettings = new ModuleManagerModuleSettings();
    moduleOptions: ModuleManagerOptions = {
        name: '', // sets the title of the module (defaults to the database entry ModuleName) - NOT TO BE USED IN SETOPTIONS
        isDefaultName: true, // flag to indicate whether the module name is set to the default name.
        customName: null, // modules with settings can be renamed. This is the custom name, given by the user. Also used for localization (by the system).
        editName: null, // model used for renaming a module. Model for input only, should be assigned to customName on apply.
        nameSuffix: null, // text to be appended to the name. NOT intended to be included in editName or customName, but immediately rendered after customName in the HTML.
        enableWidthClass: false, // enabling this will add mediaquery-like classes to 'moduleResponsiveContainer'
        hasRefresh: false, // adds a menu item for refresh
        widgetMode: false, // enabling this disables the module header and moves the menu to the body of the module
        onSettingsApplied: (s) => {
            // settings applied was called before the child module was ready for it, set a flag to
            // call it when ready
            this.container.settingsWaitingApply = s;
        },
        onRefresh: null, // function called when module container refreshes

        // allows overriding of the refreshType (should not generally be used once refreshType is set based on metrics)
        // valid values: auto, default (90s), cas (20s), casShort (10s), veryShort (5s), false
        // (false disables refresh for this module, not necessary but may very slightly improve performance)
        refreshType: ModuleManagerRefreshType.Auto,

        // allows storing data into memory for modules that are being transferred between dashboards
        // this is a function that should return the data needing to be transferred
        getTransferObject: null,

        // a function that will be called when transfer data is being loaded
        onTransferLoaded: null,
    };

    /**
     * Event that is triggered when global filters are applied. Used to reload the teams toolbar without needing to open the widget settings menu.
     */
    public onTeamsChanged: Subject<void> = new Subject<void>();

    private _showLoader = false;
    get showLoader() {
        return this._showLoader;
    }
    set showLoader(value) {
        if (value !== this._showLoader) {
            if (!NgZone.isInAngularZone()) {
                this.container.ngZone.run(() => this._showLoader = value);
            } else {
                this._showLoader = value;
            }
        }
    }

    private _customSettingsAdded = false;

    private isSharedURL: boolean;
    private sharedKey: string;

    /**
     * Tells the module container that all settings have been added so it can safely load settings.
     */
    get customSettingsAdded() {
        return this._customSettingsAdded;
    }
    set customSettingsAdded(val: boolean) {
        if (this._customSettingsAdded !== val && val === true)
            this.loadSettings(!this.container.settingsOpen && !this.moduleSettings.isNewModule)
        this._customSettingsAdded = val;
    }

    private _stateLoadPromise: Promise<Record<string, any>> = null;
    private dividerIndex = 1;

    constructor(
        public container: ModuleContainerComponent
    ) {
        this.isSharedURL = window.location.pathname.includes('SharedDashboard.aspx');
        if (this.isSharedURL) {
            const urlOptions = new Map<string, string>();
            if (window.location.search) {
                // break up url
                const queriedOptions = window.location.search.split('&');
                queriedOptions.forEach(option => {
                    const kvPair = option.split('=');
                    urlOptions.set(kvPair[0].replace('?',''), kvPair[1]);
                });
            }
            if (urlOptions.has('id')) {
                this.sharedKey = urlOptions.get('id');
            }
        }
    }

    /**
     * Add a new custom setting to this module
     *
     * @param newSetting
     * @memberof ModuleManager
     */
    async addSetting(newSetting: ModuleManagerSetting<any>) {
        this.customSettings[newSetting.name] = newSetting;

        let settingValue = null;
        try {
            settingValue = await this.getStateFromFirstLoad(newSetting.name);
        } finally {
            this.triggerSettingLoaded(newSetting.name, settingValue);
        }
    }

    /**
     * Reloads all settings from the module state on the server
     *
     * @param apply If true, onSettingsApplied() will be called when complete
     * @memberof ModuleManager
     */
    async loadSettings(apply: boolean = true) {
        const promisesToWait = [];

        if (this.container.moduleInfo.options.hasGenericSettings
            && (this.container.hasFirstSettingsOpened || this.container.genericSettingsData == null)
        ) {
            promisesToWait.push(this.loadGenericSettings());
        }

        // if first load: use already loaded if available, otherwise always load
        if (this.loadedStateValues == null || !this.container.isFirstSettingsOpen) {
            promisesToWait.push(this.loadAllStates());
        }

        await Promise.all(promisesToWait);

        // build settings object
        for (const key of Object.keys(this.customSettings ?? {})) {
            const setting = this.customSettings[key];
            const settingValue = this.loadedStateValues[key];
            this.triggerSettingLoaded(setting.name, settingValue);
        }

        if (apply) {
            // call settings applied if there are no custom settings
            this.container.hasSettingsApplied = true;
            this.moduleOptions.onSettingsApplied(this.moduleSettings);
        }
    }

    /**
     * force settings to load for components using front end group selector
     */
    async forceLoadSettings() {
        this.container.hasFirstSettingsOpened = true;
        this.container.genericSettingsLoading = false;
        await this.loadSettings(false);
    }

    public async setMaxDateRange(dateRange: number) {
        this.maxDateRange = dateRange;

        this.addState({
            name: 'maxDateRange',
            getStateData: () => dateRange,
        });

        await this.saveState('maxDateRange');
    }

    public setMinDate(days: number) {
        this.minDate = new Date().addDays(days);
    }

    setControlSettings(controlSettings: ModuleManagerModuleSettings) {
        controlSettings.selectedDateRange = ModuleManagerDateRangeInfo.fromObject(controlSettings.selectedDateRange);
        Object.assign(this.moduleSettings, controlSettings);
        this.container.onSettingsUpdated();
    }

    async reloadControlSettings(apply: boolean = true) {
        const result = await this.container.moduleContainerService.loadControlSettings(this.container.moduleId, this.container.moduleKey);
        this.setControlSettings(result.data);

        if (apply) {
            this.container.hasSettingsApplied = true;
            this.moduleOptions.onSettingsApplied(this.moduleSettings);
        }
    }

    /**
     * Retrieves the generic controls from the backend
     *
     * @memberof ModuleManager
     */
    async loadGenericSettings() {
        if (this.container.settingsLoading && this.container.genericSettingsLoading)
            return;
        this.container.settingsLoading = true;
        this.container.genericSettingsLoading = true;
        this.container.genericSettingsLoaded = false;

        try {
            const result = await this.container.moduleContainerService.loadGenericSettings(
                this.container.moduleId,
                this.container.moduleKey
            );
            this.setControlSettings(result.data.controlSettings);
            result.data.availableDateRanges = result.data.availableDateRanges.map(dr => ModuleManagerDateRangeInfo.fromObject(dr));
            this.container.availableDateRanges = result.data.availableDateRanges;
            this.container.genericSettingsData = result.data;
            this.container.genericSettingsLoaded = true;
        } finally {
            this.container.settingsLoading = false;
            this.container.genericSettingsLoading = false;
        }
    }

    /**
     * Provide access to container.settingsLoading through module-manager
     */
    set settingsLoading(loading: boolean) {
        if (!NgZone.isInAngularZone())
            this.container.ngZone.run(() => this.container.settingsLoading = loading);
        else
            this.container.settingsLoading = loading;
    }

    /**
     * Load every state belonging to this module at once
     *
     * @returns The state object
     * @memberof ModuleManager
     */
    async loadAllStates() {
        if (!this.container.moduleKey) {
            this.loadedStateValues = {};
            return {};
        }

        if (this._stateLoadPromise == null) {
            this._stateLoadPromise = this.container.moduleContainerService.loadModuleStates(this.container.moduleKey, this.isSharedURL, this.sharedKey).then((states) => {
                this._stateLoadPromise = null;
                this.loadedStateValues = states;
                return states;
            });
        }

        return this._stateLoadPromise;
    }

    /**
     * Add a state for this module to track.
     *
     * @param newState
     * @memberof ModuleManager
     */
    async addState(newState: ModuleManagerState<any>) {
        this.moduleStates[newState.name] = newState;
        this.previousStateValues[newState.name] = null;

        if (newState.autoLoad) {
            // now load the state
            if (!newState.isGlobal) {
                let stateData = null;
                try {
                    stateData = await this.getStateFromFirstLoad(newState.name);
                } finally {
                    this.triggerStateLoaded(newState.name, stateData);
                }
            } else {
                // global states shouldn't be loaded from first load
                await this.loadState(newState.name);
            }
        }
    }

    /**
     * Manually load a single state directly from the server.
     * This will trigger `onStateLoaded()`
     *
     * @param stateName The state to be loaded
     * @memberof ModuleManager
     */
    async loadState(stateName: string) {
        const state = this.moduleStates[stateName];

        let key = this.container.moduleKey;
        if (state.isGlobal)
            key = this.container.moduleContainerService.getGlobalKey(this.container.moduleId);


        let stateData: string;

        // check if we have data from a transfer to load
        if (this.container.transferData != null && this.container.transferData.states[stateName] != null) {
            stateData = JSON.stringify(this.container.transferData.states[stateName]);
            delete this.container.transferData[stateName];
        } else {
            const result = await this.container.moduleContainerService.loadModuleState(key, stateName);
            stateData = result.data;
        }

        this.triggerStateLoaded(state.name, stateData);
    }

    /**
     * Saves all custom settings to the server
     * A setting will be ignored if an exception is thrown in its `getValue()` function
     *
     * @memberof ModuleManager
     */
    async saveSettings() {
        const settingsToSave = {};
        for (const settingName of Object.keys(this.customSettings ?? {})) {
            const setting = this.customSettings[settingName];
            try {
                settingsToSave[settingName] = JSON.stringify(setting.getValue());
                this.loadedStateValues[settingName] = settingsToSave[settingName]; // overwrite the loaded value
            } catch {
                delete settingsToSave[settingName];
            }
        }

        return await this.container.moduleContainerService.saveModuleStates(this.container.moduleKey, settingsToSave);
    }

    /**
     * Save all passed in states to the server
     * A state will be ignored if an exception is thrown in its `getStateData()` function
     *
     * @param stateNames The state(s) to be saved
     * @memberof ModuleManager
     */
    async saveState(...stateNames: string[]) {
        if (this.isSharedURL)
            return false; // don't save states for url shared dashboards, they should be read only
        if (stateNames.length <= 0) { // no parameters means all states
            stateNames = Object.keys(this.moduleStates ?? {});
        }

        const stateData = {};
        const globalStateData = {};
        let hasGlobal = false;
        stateNames.forEach((stateName) => {
            const state = this.moduleStates[stateName];
            try {
                const stateValue = JSON.stringify(state.getStateData());
                let prevState = JSON.stringify(this.previousStateValues[stateName]);

                // Normalize prevState to account for schema drift in older stored documents:
                // 1. "pinned":"" was saved instead of "pinned":null before an AG Grid upgrade.
                // 2. "rowGroup":false,"rowGroupIndex":null was emitted unconditionally by CXCV-48744
                //    for non-grouped columns; the serializer now omits these inactive defaults.
                if (prevState !== undefined && prevState !== null) {
                    prevState = prevState.replace(/"pinned":""/g, '"pinned":null');
                    prevState = prevState.replace(/,"rowGroup":false,"rowGroupIndex":null/g, '');
                }

                if (stateValue === prevState || stateValue == null) {
                    // skip this state
                    return;
                }

                if (!state.isGlobal) {
                    stateData[stateName] = stateValue;
                } else {
                    globalStateData[stateName] = stateValue;
                    hasGlobal = true;
                }
            } catch { // if getStateData() throws an exception, we do not save that state
                delete stateData[stateName]; // ensure the state is not on the object of states to save
            }
        });

        // global states save to a different module key
        if (hasGlobal) {
            const globalKey = this.container.moduleContainerService.getGlobalKey(this.container.moduleId);
            await this.container.moduleContainerService.saveModuleStates(globalKey, globalStateData);

            // update previous state
            for (const stateName of Object.keys(globalStateData ?? {})) {
                this.previousStateValues[stateName] = JSON.parse(globalStateData[stateName]);
            }
        }

        if (JSON.stringify(stateData) === '{}')
            return false; // nothing to save

        await this.container.moduleContainerService.saveModuleStates(this.container.moduleKey, stateData);
        // update previous state
        for (const stateName of Object.keys(stateData ?? {})) {
            this.previousStateValues[stateName] = JSON.parse(stateData[stateName]);
        }
        return true;
    }

    /**
     * Add an item to the ellipsis menu of the module header.
     * Passing in 'divider' will create a divider.
     *
     * @param menuItem
     * @memberof ModuleManager
     */
    addMenuItem(menuItem: ModuleManagerMenuItem | string) {
        if (menuItem !== null) {
            if (menuItem === 'divider') { // shortcut to add a divider
                menuItem = {
                    key: 'divider' + this.dividerIndex++,
                    name: '',
                    icon: '',
                    type: ModuleManagerMenuItemType.Divider,
                    click: () => { },
                    readOnlyEnabled: true,
                } as ModuleManagerMenuItem;
            } else {
                menuItem = menuItem as ModuleManagerMenuItem;
            }

            if (this.hasWriteAccess() || menuItem.readOnlyEnabled) {
                menuItem = this.initMenuItem(menuItem);

                if (menuItem.placement === ModuleManagerMenuItemPlacement.Default || menuItem.placement == null) {
                    if (this.additionalMenuItems === null) {
                        this.additionalMenuItems = {};
                    }

                    this.additionalMenuItems[menuItem.key] = menuItem;
                }

                if (menuItem.placement === ModuleManagerMenuItemPlacement.AfterExport) {
                    if (this.afterExportMenuItems === null) {
                        this.afterExportMenuItems = {};
                    }

                    this.afterExportMenuItems[menuItem.key] = menuItem;
                }

            }
        }
        this.container.changeDetector.detectChanges();
    }

    /**
     * Remove all menu items
     *
     * @memberof ModuleManager
     */
    resetMenu() {
        this.additionalMenuItems = null;
        this.afterExportMenuItems = null;
    }

    /**
     * Update the module size in gridster
     *
     * @param x
     * @param y
     * @param [resizable=false]
     * @param [updateMap=true]
     * @memberof ModuleManager
     */
    setSize(x: number, y: number, resizable: boolean = false, updateMap: boolean = true) {
        const gridsterItem = this.container.gridsterItem;
        if (gridsterItem.$item.cols === x && gridsterItem.$item.rows === y)
            return;

        const push = new GridsterPush(gridsterItem); // init the service
        gridsterItem.$item.rows = y;
        gridsterItem.$item.cols = x;

        if (!resizable) {
            gridsterItem.$item.minItemRows = y;
            gridsterItem.$item.maxItemRows = y;
            gridsterItem.$item.minItemCols = x;
            gridsterItem.$item.maxItemCols = x;
        }
        else {
            gridsterItem.$item.minItemRows = 3;
            gridsterItem.$item.maxItemRows = 32;
            gridsterItem.$item.minItemCols = 3;
            gridsterItem.$item.maxItemCols = 32;
        }

        // check if the item goes over the right side of the gridster
        const totalOverWidth = (gridsterItem.$item.cols + gridsterItem.$item.x) - this.container.dashboardService.GRIDSTER_COLUMNS;
        if (totalOverWidth > 0) {
            gridsterItem.$item.x = Math.max(0, gridsterItem.$item.x - totalOverWidth);
        }

        push.pushItems(push.fromNorth);
        push.checkPushBack(); // check for items can restore to original position
        push.setPushedItems(); // save the items pushed
        gridsterItem.checkItemChanges(gridsterItem.$item, gridsterItem.item);
        push.destroy();

        if (updateMap) {
            this.container.dashboardManager.updateModuleMaps();
        }
    }

    /**
     * Set the module's minimum size in gridster
     *
     * @param x
     * @param y
     */
    setMinSize(x: number, y: number) {
        this.container.gridsterItem.$item.minItemRows = y;
        this.container.gridsterItem.$item.minItemCols = x;
    }

    /**
     * Set the module's maximum size in gridster
     *
     * @param x
     * @param y
     */
    setMaxSize(x: number, y: number) {
        this.container.gridsterItem.$item.maxItemRows = y;
        this.container.gridsterItem.$item.maxItemCols = x;
    }

    /**
     * Add a header icon to the header of the module
     *
     * @param headerItem
     * @memberof ModuleManager
     */
    addHeaderItem(headerItem: ModuleManagerHeaderItem) {
        if (headerItem) {
            this.initHeaderItem(headerItem);

            if (this.additionalHeaderItems === null) {
                this.additionalHeaderItems = {};
            }

            this.additionalHeaderItems[headerItem.name] = headerItem;
            this.container.changeDetector.detectChanges();
        }
    };

    addToolbarItem(toolbarItem: Type<IModuleManagerToolbarComponent>, context: any = undefined) {
        this.container.addToolbarItem(toolbarItem, context);
    }

    /**
     * @returns Whether or not there are any modules currently being dragged.
     * @memberof ModuleManager
     */
    areModulesDragging() {
        return this.container.moduleContainerService.modulesDragging > 0;
    }

    /**
     * @returns Whether this module should reduce CPU usage or not
     * @memberof ModuleManager
     */
    isModulePaused() {
        return this.areModulesDragging() || !this.container.dashboardManager.isActive;
    }

    /**
     * Gets the localized name for a menu item
     *
     * @param menuItemKey
     * @param valueIfFail
     * @returns
     * @memberof ModuleManager
     */
    getMenuItemName(menuItemKey: string, valueIfFail: string) {
        if (this.container.moduleContainerService.menuLocalizationKeys
            && this.container.moduleContainerService.menuLocalizationKeys.get(menuItemKey)
        ) {
            return this.container.moduleContainerService.menuLocalizationKeys.get(menuItemKey);
        } else {
            return valueIfFail;
        }
    }

    /**
     * @returns Settings set for this module
     * @memberof ModuleManager
     */
    getModuleSettings() {
        return this.moduleSettings;
    }

    /**
     * @returns Whether this module is on a dashboard with write access.
     * @memberof ModuleManager
     */
    hasWriteAccess() {
        return (this.container.standaloneMode || this.container.dashboardManager.writeAccess) &&
            !this.container.dashboardManager.isDashboardProjection;
    }

    /**
     * @returns Whether this module is on a dashboard with read group access.
     * @memberof ModuleManager
     */
    hasReadGroupAccess() {
        return this.container.standaloneMode || this.container.dashboardManager.readGroupAccess;
    }

    /**
     * @returns if a user has permission to open the Agent State Details modal or not.
     * @memberof ModuleManager
     */
    hasAgentStateDetailsAccess() {
        return this.container.dashboardManager.agentStateDetailsAccess;
    }

    /**
     * @returns The identifying key of this module.
     * @memberof ModuleManager
     */
    getModuleKey() {
        return this.container.moduleKey;
    }

    getModuleInfo() {
        return this.container.moduleInfo;
    }

    /**
     * currently this is only used to give dynamic settings for the report widget but can be expanded later on for other widgets if desired
     * @param newSettings has info related to whether this widget should have the emp or rec selectors and which levels to display as well
     */
    updateWidgetSettings(newSettings: ReportSelectorSettings) {
        const widget = this.container.moduleId;
        switch (widget) {
            case 131: // Report
            case 149: // Quality Evaluations
                this.getModuleInfo().DTO.SettingEmpSelector = newSettings.HasEmpSelector;
                this.getModuleInfo().DTO.SettingRecSelector = newSettings.HasRecSelector;
                // holding this info in the container for now to be passed through to and used by the group selector
                // in ngOnChanges which is how we pass it to the settingsManager.
                this.getModuleInfo().DTO.SettingEmpLevels = newSettings.EmpSelectorLevels;
                this.getModuleInfo().DTO.SettingRecLevels = newSettings.RecSelectorLevels;
                this.getModuleInfo().DTO.SettingDateRangeSelector = newSettings.HasDateRangeSelector;
                this.container.changeDetector.detectChanges();
                break;
            case 154: // Coaching Effectiveness
                this.getModuleInfo().DTO.SettingEmpSelector = newSettings.HasEmpSelector;
                this.getModuleInfo().DTO.SettingEmpLevels = newSettings.EmpSelectorLevels;
                this.container.changeDetector.detectChanges();
                break;
        }
    }

    get settingsView() {
        return this.container.moduleContainerService.currentSettingsView;
    }

    /**
     * Tell the dashboard that we made a change that needs to be published.
     *
     * @memberof ModuleManager
     */
    requestPublishNeeded() {
        this.container.dashboardManager.requestPublishNeeded();
    }

    preventModuleMapUpdate() {
        this.container.dashboardManager.preventModuleMapUpdate(this.container.moduleKey);
    }

    removeModuleMapPrevention() {
        this.container.dashboardManager.removeModuleMapUpdatePrevention(this.container.moduleKey);
    }

    /**
     * @returns The current row this module is in.
     * @memberof ModuleManager
     */
    getRow() {
        return this.container.gridsterItem.$item.y;
    }

    /**
     * @returns The current column this module is in.
     * @memberof ModuleManager
     */
    getColumn() {
        return this.container.gridsterItem.$item.x;
    }

    /**
     * @returns The current size of this module.
     * @memberof ModuleManager
     */
    getSize() {
        return {
            x: this.container.gridsterItem.$item.cols,
            y: this.container.gridsterItem.$item.rows
        };
    }

    getGridSize() {
        return {
            width: this.container.gridsterItem.width,
            height: this.container.gridsterItem.height
        };
    }

    /**
     * Set module manager options
     *
     * @param options
     * @memberof ModuleManager
     */
    setOptions(options: ModuleManagerOptions) {
        // do not set options.name here as it is taken care of generically in moduleManager and moduleContainer
        if (options.canRefresh !== undefined) {
            this.moduleOptions.canRefresh = options.canRefresh;
        }

        if (options.hasRefresh !== undefined) {
            this.moduleOptions.hasRefresh = options.hasRefresh && !this.isReport();
        }

        if (options.onExport !== undefined) {
            this.moduleOptions.onExport = options.onExport;
        }

        if (options.exportEnabled !== undefined) {
            this.moduleOptions.exportEnabled = options.exportEnabled;
        }

        if (options.onImageExport !== undefined) {
            this.moduleOptions.onImageExport = options.onImageExport;
        }

        if (options.imageExportVisible !== undefined) {
            this.moduleOptions.imageExportVisible = options.imageExportVisible;
        }

        if (options.enableWidthClass !== undefined) {
            if (options.enableWidthClass) {
                this.container.updateWidthClass();
            } else {
                this.container.widthClass = '';
            }
            this.moduleOptions.enableWidthClass = options.enableWidthClass;
        }

        if (options.widgetMode !== undefined) {
            this.moduleOptions.widgetMode = options.widgetMode;
        }

        if (options.onSettingsApplied !== undefined) {
            this.moduleOptions.onSettingsApplied = options.onSettingsApplied;
            // if we had settings to apply already, do that now
            if (this.container.settingsWaitingApply !== null) {
                this.container.applyWaitingSettings();
            }
        }

        if (options.onRefresh !== undefined) {
            this.moduleOptions.onRefresh = options.onRefresh;
        }

        if (options.refreshType !== undefined) {
            if(options.refreshType !== ModuleManagerRefreshType.Auto && !this.container.config.isCxOne){
                // if date ranges exist, the GenericControlSettings should apply the refreshType
                if(!this.container.moduleInfo.DTO.SettingDateRangeSelector)
                    this.moduleOptions.refreshType = options.refreshType;

                this.addState({
                    name: 'widgetRefreshType',
                    getStateData: () => options.refreshType,
                } as ModuleManagerState<string>);
                this.saveState('widgetRefreshType');
            }
            else
            {
                this.moduleOptions.refreshType = options.refreshType;
            }

            this.container.subscribeToRefresh(); // update which refresh we're subscribed to
        }

        if (options.getTransferObject !== undefined) {
            this.moduleOptions.getTransferObject = options.getTransferObject;
        }

        if (options.onTransferLoaded !== undefined) {
            this.moduleOptions.onTransferLoaded = options.onTransferLoaded;
            if (this.transferChildData != null) {
                this.container.applyTransferData();
            }
        }

        this.container.updateRefreshCountdownString();
        this.container.changeDetector.detectChanges();
    }

    /**
     * Mark this module as ready for refresh or not.
     * This should be set to true after data loads.
     *
     * @param [isRefreshReady=true] If this module is ready to be refreshed again.
     * @returns
     * @memberof ModuleManager
     */
    setAutoRefreshReady(isRefreshReady: boolean = true) {
        if (this.container.moduleDestroyed || this.isReport()) {
            return;
        }

        this.container.refreshReady = isRefreshReady;

        // Update the time displayed in the module's title bar:
        let timezone = "";
        const notSet = "Not Set";

        // Time zone is set for the dashboard
        if (this.container.dashboardManager.timeZone)
            timezone = this.container.dashboardManager.timeZone;
        // Time zone is set for the user
        else if ((this.container.timeZoneService.dataObj?.selectedTimeZone ?? notSet) !== notSet)
            timezone = this.container.timeZoneService.dataObj.selectedTimeZone;
        // Time zone is set for the company
        else if ((this.container.timeZoneService.dataObj?.defaultCompanyTimeZone ?? notSet) !== notSet)
            timezone = this.container.timeZoneService.dataObj.defaultCompanyTimeZone;

        this.container.lastRefreshTime = new Date();
        const offset = this.container.timeZoneService.getTimezoneOffset(timezone);
        const lastRefreshTime = new Date(this.container.lastRefreshTime);
        this.container.lastRefreshServerTime = lastRefreshTime.addMinutes(offset);
        this.container.isRefreshing = false;

        if (isRefreshReady) {
            this.numRetries = 0; // reset load retry count
            this.container.somethingWentWrong = false;
            this.container.networkConnectionLost = false;
            if (!this.container.dashboardManager?.IsIdle) // wait to subscribe when no longer idle
                this.container.subscribeToRefresh();
        } else {
            this.container.unsubscribeRefresh();
        }
    }

    /**
     * @returns Whether this module is on a report dashboard
     * @memberof ModuleManager
     */
    public isReport(): boolean {
        return this.container.dashboardService?.isReportDashboard?.() === true ||
            this.container.dashboardManager?.IsCustomReport === true;
    }

    async handleLoadError(errorResponse, wasAuto = false) {
        // retry load if we haven't retried
        if (this.numRetries++ <= ModuleManager.MAX_LOAD_RETRIES) {
            this.container.nextRefresh = Date.now() + ModuleManager.TIME_BETWEEN_AUTO_RETRY;
            await ClearViewUtils.timeout(ModuleManager.TIME_BETWEEN_AUTO_RETRY);
            this.moduleOptions.onRefresh(wasAuto);
            return;
        } else {
            // show loader for 2 seconds between retry clicks
            await ClearViewUtils.timeout(2000);
        }

        this.showLoader = false;

        // commented out in case we decide to do something different for auto refresh
        // if (wasAuto) {
        //     this.setAutoRefreshReady(true);
        //     return;
        // }

        if (errorResponse.status === 500 || errorResponse.status_code === 500)
            // Exception from server (500)
            this.container.somethingWentWrong = true;
        else if (errorResponse.status === 403 || errorResponse.status_code === 403)
            // Forbidden error thrown from server
            this.container.accessDenied = true;
        else if (errorResponse.status !== undefined || errorResponse.status_code !== undefined)
            // Other thrown error from server (most likely unauthorized)
            this.container.somethingWentWrong = true;
        else
            // Response without exception, probably timeout
            this.container.networkConnectionLost = true;
    }

    destroy() {
    }

    /**
     * Register or deregister multiple events
     *
     * @param events
     * @returns
     * @memberof ModuleManager
     */
    registerEvents(events: false | { [eventName: string]: (...args) => void }) {
        if (events === undefined) {
            console.warn('Calling registerEvents() with no parameters does nothing');
            return;
        }

        // passing in false will deregister ALL events
        if (events === false) {
            for (const eventName of Object.keys(this.moduleEvents ?? {})) {
                const eventItem = this.moduleEvents[eventName];
                if (eventItem !== null && eventItem.deregister !== undefined) {
                    // any events that we create with $scope.$on will be given a deregister() function to remove the event
                    eventItem.deregister();
                }

                this.moduleEvents[eventName] = null;
            }
            return;
        }

        // these arrays are to distinguish between events that we are already registered to and events that need to be registered
        const containerEvents = [
            'onGainedFocus',
            'onLostFocus',
            'onResizeStop',
            'onDragStart',
            'onDragStop',
            'onDashboardActivated',
            'onDashboardDeactivated',
            'onSettingsOpened',
            'onSettingsCancelled',
        ];
        const eventsToRegister = [
            'onResizeStart',
            'onResize',
            'onFullscreenToggle',
        ];

        // set up any events that the ModuleContainer is already listening to
        for (const eventName of containerEvents) {
            if (events[eventName] !== undefined) { // if this event wasn't set, don't do anything with it
                if (!events[eventName]) {
                    // event was passed in as false so disable the event
                    this.moduleEvents[eventName] = null;
                } else {
                    // assign the event handle
                    this.moduleEvents[eventName] = {
                        handle: events[eventName]
                    } as ModuleManagerEvent;
                }
            }
        }

        // register any events that the ModuleContainer isn't already listening to
        for (const eventName of eventsToRegister) {
            if (events[eventName] !== undefined) {
                if (this.moduleEvents[eventName] != null) {
                    // this event is already set and we're replacing it so deregister it
                    this.moduleEvents[eventName].deregister();
                }

                if (!events[eventName]) {
                    // event was set to false so disable it (we already deregistered it above)
                    this.moduleEvents[eventName] = null;
                } else {
                    // setup the event to be registered
                    let deregister: () => void = null;

                    // different events may need different checks and arguments
                    // so we switch through to allow this
                    // setting broadcastName will use the default handler, listening to $scope.$on for that name
                    let broadcastName = eventName;
                    switch (eventName) {
                        case 'onResizeStart':
                            broadcastName = 'moduleResizeStart';
                            break;
                        case 'onResize':
                            broadcastName = 'moduleResizing';
                            break;
                        case 'onFullscreenToggle':
                            broadcastName = 'moduleFullscreenToggled';
                            break;
                        // if an event needs a special event handler, assign the $scope.$on to deregister here
                        // and the default event handler won't be used
                        // example:
                        case 'myEvent':
                            deregister = this.container.registerEvent('mySpecialEvent', (arg1, arg2) => {
                                this.moduleEvents[eventName].handle(arg1, arg2);
                            });
                            break;
                    }

                    if (deregister == null) { // default event handler
                        deregister = this.container.registerEvent(broadcastName, (ev, $e, $w) => {
                            // check module key matches
                            const moduleKey = $w.find('module-container').attr('mkey');
                            if (moduleKey === this.container.moduleKey) {
                                this.moduleEvents[eventName].handle(ev, $e, $w);
                            }
                        });
                    }

                    // setup the event variables
                    this.moduleEvents[eventName] = {
                        handle: events[eventName],
                        deregister
                    };
                }
            }
        }

        // Call the onSettingsOpened event here, since it may have occurred before the settings controller was constructed.
        // Some module settings (Current Agent States, for one) depend on it to load their data
        if (this.moduleEvents.onSettingsOpened && this.container.isFirstSettingsOpen) {
            this.moduleEvents.onSettingsOpened.handle(true);
        }
    }

    /**
     * Creates an object to be held in memory while a module is in the taskbar.
     *
     * @returns
     * @memberof ModuleManager
     */
    getTransferObject() {
        const moduleData = new ModuleManagerTransferData();
        moduleData.moduleID = this.container.moduleId;
        moduleData.moduleKey = this.container.moduleKey;
        moduleData.childModule = this.container.module.ModuleSource;
        moduleData.moduleInfo = Object.assign({}, this.container.moduleInfo);
        moduleData.moduleInfo.moduleKey = this.container.moduleKey;

        // states
        moduleData.states = {};
        for (const stateKey of Object.keys(this.moduleStates ?? {})) {
            moduleData.states[stateKey] = this.moduleStates[stateKey].getStateData();
        }

        // settings
        moduleData.settings = {};
        for (const settingKey of Object.keys(this.customSettings ?? {})) {
            moduleData.settings[settingKey] = this.customSettings[settingKey].getValue();
        }

        // child object (if set by child module)
        if (this.moduleOptions.getTransferObject != null) {
            moduleData.childObject = this.moduleOptions.getTransferObject();
        }

        return moduleData;
    }

    /**
     * Set a suffix for the module
     *
     * @param value
     * @param [autoFormat=false] If true, the suffix will be prepended with " - "
     * @memberof ModuleManager
     */
    setModuleNameSuffix(value: string, autoFormat: boolean = false) {
        if (autoFormat) {
            this.moduleOptions.nameSuffix = " - " + value;
        } else {
            this.moduleOptions.nameSuffix = value;
        }

        this.container.changeDetector.detectChanges();
    }

    /**
     * Clears out any suffix set on the module
     *
     * @memberof ModuleManager
     */
    removeModuleNameSuffix() {
        this.moduleOptions.nameSuffix = null;
        this.container.changeDetector.detectChanges();
    }

    async drillDown(groupId: number = null) {
        try {
            this.showLoader = true;
            await this.container.moduleHelperService.drillDownToGroup(this.container.moduleKey, groupId);
            this.requestPublishNeeded();
            if (this.container.hasFirstSettingsOpened)
                await this.loadSettings(true);
            else
                await this.reloadControlSettings(true); // only refresh control settings because settings have not opened yet
        } catch (e) {
            console.error(e);
        }
    }

    /**
     * Internal method to set the header item
     *
     * @private
     * @param headerItem
     * @memberof ModuleManager
     */
    private initHeaderItem(headerItem: ModuleManagerHeaderItem) {
        // allows headerItems to define a key
        if (headerItem.name === undefined) {
            headerItem.name = headerItem.icon;
        }
    }

    /**
     * Runs initialization logic on a menu item
     *
     * @private
     * @param menuItem
     * @returns The initialized menuItem
     * @memberof ModuleManager
     */
    private initMenuItem(menuItem: ModuleManagerMenuItem) {
        if (menuItem.getEnabled === undefined) {
            // always enabled if getEnabled() isn't set
            menuItem.getEnabled = () => true;
        }

        // allows menuItems to define a key
        if (menuItem.key === undefined) {
            menuItem.key = menuItem.name;
        }

        if (menuItem.localizedName === undefined) {
            menuItem.localizedName = menuItem.name;
        }

        // always adds additional menu items to the default container if placement isn't set
        if (menuItem.placement === undefined) {
            menuItem.placement = ModuleManagerMenuItemPlacement.Default;
        }

        if (!menuItem.type) {
            menuItem.type = ModuleManagerMenuItemType.MenuItem;
        } else if (menuItem.type === ModuleManagerMenuItemType.SubMenu) {
            // sub menu specific
            const baseItem = menuItem as ModuleManagerSubMenu;
            const subMenu = new ModuleManagerSubMenu(
                menuItem.key,
                menuItem.name,
                menuItem.localizedName,
                menuItem.icon,
                menuItem.type,
                menuItem.placement,
                menuItem.inputType,
                menuItem.click,
                menuItem.getEnabled,
                menuItem.readOnlyEnabled
            );
            subMenu.subItems = baseItem.subItems;

            // initalize any sub items in this menu
            if (subMenu.subItems != null) {
                for (let i = 0; i < subMenu.subItems.length; i++) {
                    subMenu.subItems[i] = this.initMenuItem(subMenu.subItems[i]);
                }
            }

            menuItem = subMenu;
        } else if (menuItem.type === ModuleManagerMenuItemType.CheckMenu || menuItem.type === ModuleManagerMenuItemType.RadioCheckMenu) {
            const baseItem = menuItem as ModuleManagerCheckMenu;
            const subMenu = new ModuleManagerCheckMenu(
                menuItem.key,
                menuItem.name,
                menuItem.localizedName,
                menuItem.icon,
                menuItem.type,
                menuItem.placement,
                menuItem.inputType,
                menuItem.click,
                menuItem.getEnabled,
                menuItem.readOnlyEnabled
            );

            if (baseItem.getItemName !== undefined) {
                subMenu.getItemName = baseItem.getItemName;
                // if menu has a getItemName function, run it through localization
                subMenu.getLocalizedName = (i) => {
                    const itemName = subMenu.getItemName(i);

                    if (this.container.moduleContainerService.menuLocalizationKeys != null
                        && this.container.moduleContainerService.menuLocalizationKeys.get(itemName)
                    ) {
                        return this.container.moduleContainerService.menuLocalizationKeys.get(itemName);
                    }

                    return itemName;
                };
            }

            subMenu.getCheckItems = baseItem.getCheckItems;
            subMenu.isItemChecked = baseItem.isItemChecked;
            subMenu.getItemInputType = baseItem.getItemInputType;
            subMenu.onCheckClicked = baseItem.onCheckClicked;
            menuItem = subMenu;
        }

        // localize non-divider keys
        if (menuItem.type !== ModuleManagerMenuItemType.Divider) {
            if (this.container.moduleContainerService?.menuLocalizationKeys
                && this.container.moduleContainerService.menuLocalizationKeys.get(menuItem.name)
            ) {
                menuItem.localizedName = this.container.moduleContainerService.menuLocalizationKeys.get(menuItem.name);
            }
        }

        return menuItem;
    }

    /**
     * Gets a state value from the first bulk state load, if available.
     *
     * @private
     * @param stateName The name of the state to be retrieved.
     * @returns The state data
     * @memberof ModuleManager
     */
    private async getStateFromFirstLoad(stateName: string) {
        // if we're still waiting for states to load, add us to the wait
        if (this.loadedStateValues == null) {
            await this.loadAllStates();
        }

        return this.loadedStateValues[stateName];
    }

    /**
     * Parses the setting value and sends it to the module
     *
     * @private
     * @param settingName The name of the setting to be triggered
     * @param settingValue The value of the setting
     * @memberof ModuleManager
     */
    private triggerSettingLoaded(settingName: string, settingValue: any) {
        const setting = this.customSettings[settingName];

        let parsedData;
        try {
            parsedData = JSON.parse(settingValue);
        } catch {
            parsedData = settingValue;
        }

        if (parsedData == null) {
            parsedData = setting.defaultValue;
        }

        this.moduleSettings[settingName] = parsedData;
        setting.onSettingLoaded(deepcopy(parsedData));
    }

    /**
     * Parses the state value and sends it to the module
     *
     * @private
     * @param stateName The name of the state to be triggered
     * @param stateData The value of the state
     * @memberof ModuleManager
     */
    private triggerStateLoaded(stateName: string, stateData: any) {
        const state = this.moduleStates[stateName];

        let parsedData;
        try {
            parsedData = JSON.parse(stateData);
        } catch {
            parsedData = stateData;
        }

        this.previousStateValues[stateName] = parsedData;
        state.onStateLoaded(deepcopy(parsedData));
    }

    /**
     * 
     * @returns if an CXOne user has coaching manager access
     * @memberof ModuleManager
     */
    hasCXOneCoachingManagerAccess() {
        return this.container.dashboardManager.cXOneCoachingManagerAccess;
    }

    /**
     * 
     * @returns whether a user is allowed to award agents
     * @memberof ModuleManager
     */
    hasGiveAwardManagerAccess() {
        return this.container.dashboardManager.giveAwardManagerAccess;
    }

    /**
     * 
     * @returns whether a user is allowed to suggest coaching sessions
     * @memberof ModuleManager
     */
    hasSuggestCoachingAccess() {
        return this.container.dashboardManager.suggestCoachingAccess;
    }

    /**
     * 
     * @returns whether a user is allowed to force logout agents
     * @memberof ModuleManager
     */
    hasForceLogoutAccess() {
        return this.container.dashboardManager.forceLogoutAccess;
    }

    /**
     * 
     * @returns whether a user is allowed to terminate agents
     * @memberof ModuleManager
     */
    hasTerminateAccess() {
        return this.container.dashboardManager.terminateAccess;
    }
}
